import { memo, ChangeEvent, useMemo } from 'react';
import cn from 'classnames';
import { Props } from './types';
import styles from './style.module.css';
import { Form, Input, Checkbox, Select } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { ROLES } from '../../../components/Users/constants';
import { useFilterOption } from '../../../hooks/shared/useFilterOption';
import { usePermissions } from '../../../hooks/shared/usePermissions';
import { UserPasswordField } from './UserPasswordField';
import { ClinicSelect } from '../../../components/shared/ClinicSelect';

const PopupForm = memo(
  ({
    formId,
    onSubmit,
    roles,
    clinics,
    login,
    onLoginChange,
    password,
    onPasswordChange,
    onPasswordGenerated,
    role,
    onRoleChange,
    clinic,
    onClinicChange,
    disabled,
    onEnabledChange,
    errors,
    isEditing,
    isAdmin,
    selectedUser,
    handleFocus,
    submitting = false,
  }: Props) => {
    const filterOption = useFilterOption();
    const { canAssignAdministratorRole } = usePermissions();

    const enabled = useMemo(() => !disabled, [disabled]);

    const rolesArray = useMemo(() => {
      const shouldPreserveSelectedAdminRole =
        !canAssignAdministratorRole &&
        isEditing &&
        role === selectedUser?.role?.id &&
        selectedUser?.role?.name === 'Admin';

      return roles
        .filter((roleItem) => {
          if (roleItem.name !== 'Admin') {
            return true;
          }

          return (
            canAssignAdministratorRole ||
            (shouldPreserveSelectedAdminRole && roleItem.id === role)
          );
        })
        .map((roleItem) => {
          return {
            value: roleItem.id,
            id: roleItem.id,
            label: ROLES[roleItem.name],
            disabled: roleItem.name === 'Admin' && !canAssignAdministratorRole,
          };
        });
    }, [
      canAssignAdministratorRole,
      isEditing,
      role,
      roles,
      selectedUser?.role?.id,
      selectedUser?.role?.name,
    ]);

    const handleLoginChange = (event: ChangeEvent<HTMLInputElement>) => {
      if (!onLoginChange) return;

      handleFocus('userName');
      onLoginChange(event);
    };

    const handleRoleChange = (value: string) => {
      handleFocus('role');
      onRoleChange(value);
    };

    const handleClinicChange = (value: string[]) => {
      handleFocus('clinic');
      onClinicChange(value);
    };

    const handleEnabledChange = (event: CheckboxChangeEvent) => {
      onEnabledChange(!event.target.checked);
    };

    return (
      <>
        <Form
          id={formId}
          name="validateOnly"
          layout="vertical"
          autoComplete={isEditing ? 'off' : 'on'}
          onFinish={onSubmit}
          onFinishFailed={onSubmit}
        >
          {isEditing ? (
            <p className={styles.titleModal}>
              Редактировать пользователя {login}
            </p>
          ) : (
            <p className={styles.titleModal}>Добавить пользователя</p>
          )}
          {isEditing ? null : (
            <p className={styles.subtitleModal}>
              Создайте новую учетную запись пользователя системы
            </p>
          )}
          <div className={cn(styles.reportMain, styles.userModal)}>
            {isEditing ? null : (
              <div className={styles.selectReport}>
                <label
                  className={styles.titleSelect}
                  htmlFor="new-user-username"
                >
                  Логин <span className={styles.requiredMark}>*</span>
                </label>
                <Form.Item
                  name="userName"
                  validateStatus={
                    errors.userName ? errors.userName.validateStatus : ''
                  }
                  help={errors.userName ? errors.userName.help : ''}
                  rules={[
                    {
                      required: true,
                      message: 'Необходимо указать логин',
                      type: 'string',
                    },
                  ]}
                >
                  <Input
                    id="new-user-username"
                    name="username"
                    autoComplete="username"
                    placeholder="Введите логин"
                    value={login}
                    onChange={onLoginChange}
                    onFocus={() => handleFocus('userName')}
                    disabled={submitting}
                  />
                </Form.Item>
              </div>
            )}
            {isEditing ? null : (
              <UserPasswordField
                password={password}
                onPasswordChange={onPasswordChange}
                onPasswordGenerated={onPasswordGenerated}
                handleFocus={handleFocus}
                errors={errors}
                submitting={submitting}
              />
            )}

            <div className={styles.selectReportRow}>
              <div className={styles.rowColumn}>
                <label className={styles.titleSelect}>
                  Роль <span className={styles.requiredMark}>*</span>
                </label>
                <Form.Item
                  className={styles.formItem}
                  validateStatus={errors.role ? errors.role.validateStatus : ''}
                  help={errors.role ? errors.role.help : ''}
                  rules={[
                    {
                      required: true,
                      message: 'Необходимо выбрать роль',
                      type: 'string',
                    },
                  ]}
                >
                  <Select
                    className={styles.input}
                    placeholder="Все"
                    value={role}
                    filterOption={filterOption}
                    onChange={handleRoleChange}
                    removeIcon={null}
                    onOpenChange={() => handleFocus('role')}
                    maxTagCount="responsive"
                    maxTagTextLength={24}
                    options={rolesArray}
                    autoClearSearchValue={false}
                    disabled={submitting}
                    optionRender={(state) => (
                      <span className={styles.inputSpan}> {state.label}</span>
                    )}
                  />
                </Form.Item>
              </div>
              <div className={styles.rowColumn}>
                <label className={styles.titleSelect}>
                  Медицинская организация{' '}
                  <span className={styles.requiredMark}>*</span>
                </label>
                <Form.Item
                  className={styles.formItem}
                  validateStatus={
                    errors.clinic ? errors.clinic.validateStatus : ''
                  }
                  help={errors.clinic ? errors.clinic.help : ''}
                  rules={[
                    {
                      required: true,
                      message:
                        'Необходимо выбрать хотя бы одну медицинскую организацию',
                      type: 'string',
                    },
                  ]}
                >
                  {isAdmin ? (
                    <ClinicSelect
                      clinics={clinics}
                      className={styles.input}
                      value={clinic || []}
                      onChange={handleClinicChange}
                      selectAllBehavior="select-all"
                      disabled={true}
                      showOnlyAllOption={true}
                    />
                  ) : (
                    <ClinicSelect
                      clinics={clinics}
                      className={styles.input}
                      placeholder="Все"
                      value={clinic || []}
                      onChange={handleClinicChange}
                      selectAllBehavior="select-all"
                      filterOption={filterOption}
                      onOpenChange={() => handleFocus('clinic')}
                      disabled={submitting}
                      optionRender={(state) => (
                        <span className={styles.inputSpan}> {state.label}</span>
                      )}
                    />
                  )}
                </Form.Item>
              </div>
            </div>
            <div className={styles.selectReport}>
              <Checkbox
                className={styles.checkboxReportFields}
                onChange={handleEnabledChange}
                checked={enabled}
                disabled={submitting}
              >
                Активен
              </Checkbox>
            </div>
          </div>
        </Form>
      </>
    );
  },
);

PopupForm.displayName = 'PopupForm';
export default PopupForm;
